import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import RemoveIcon from '@mui/icons-material/Remove'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import { Fragment, useState } from 'react'
import { useInvoiceAuditLog } from '../../api/react-query'
import { YYYY_MM_DD } from '../../tiger/Constants'

type WhoBO = { type: 'BO'; name: string; authPersonId: string }
type WhoBackofficeUser = { type: 'BACKOFFICE_USER'; email: string; roles: string }
type WhoStandaloneUser = { type: 'STANDALONE_USER'; personId: string; authPersonId: string; fullName: string }
type WhoSystem = { type: 'SYSTEM' }
type WhoUnknown = { type: 'UNKNOWN' }

export type Who = WhoBO | WhoBackofficeUser | WhoStandaloneUser | WhoSystem | WhoUnknown

export const mapWho = (raw: unknown): Who => {
  if (!raw || typeof raw !== 'object' || !('type' in raw)) return { type: 'UNKNOWN' }

  const obj = raw as Record<string, unknown>

  switch (obj.type) {
    case 'BO':
      return { type: 'BO', name: String(obj.name ?? ''), authPersonId: String(obj.authPersonId ?? '') }
    case 'BACKOFFICE_USER':
      return { type: 'BACKOFFICE_USER', email: String(obj.email ?? ''), roles: String(obj.roles ?? '') }
    case 'STANDALONE_USER':
      return {
        type: 'STANDALONE_USER',
        personId: String(obj.personId ?? ''),
        authPersonId: String(obj.authPersonId ?? ''),
        fullName: String(obj.fullName ?? ''),
      }
    case 'SYSTEM':
      return { type: 'SYSTEM' }
    default:
      return { type: 'UNKNOWN' }
  }
}

export const formatWho = (raw: unknown): string => {
  const who = mapWho(raw)

  switch (who.type) {
    case 'BO':
      return who.name || who.authPersonId
    case 'BACKOFFICE_USER':
      return who.email
    case 'STANDALONE_USER':
      return who.fullName || who.personId
    case 'SYSTEM':
      return 'System'
    case 'UNKNOWN':
      return 'Unknown'
  }
}

type InvoiceAuditLogDialogProps = {
  open: boolean
  onClose: () => void
  customerId: string
  invoiceId: string
}

const AuditLogContent = ({ auditLogs, isLoading }: { auditLogs: any[]; isLoading: boolean }) => {
  if (isLoading) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          padding: 4,
        }}
      >
        <CircularProgress />
      </Stack>
    )
  }

  if (auditLogs.length === 0) {
    return (
      <Typography
        sx={{
          padding: 2,
          color: 'text.secondary',
        }}
      >
        No audit log entries found for this invoice.
      </Typography>
    )
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>Date</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Who</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {auditLogs.map((log) => (
            <AuditLogRow key={log.id} log={log} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const AuditLogRow = ({ log }: { log: any }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <Fragment>
      <TableRow>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.when ? dayjs(log.when).format(`${YYYY_MM_DD} HH:mm`) : '-'}</TableCell>
        <TableCell>{log.action?.type ?? '-'}</TableCell>
        <TableCell>{log.action?.description ?? '-'}</TableCell>
        <TableCell>{formatWho(log.who)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ padding: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded}>
            <pre style={{ margin: 0, padding: '12px 16px', fontSize: '12px', overflow: 'auto', background: '#f5f5f5' }}>
              {JSON.stringify(log, null, 2)}
            </pre>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  )
}

export const InvoiceAuditLogDialog = ({ open, onClose, customerId, invoiceId }: InvoiceAuditLogDialogProps) => {
  const { data: auditLogs = [], isLoading } = useInvoiceAuditLog(customerId, invoiceId, open)

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Audit Log
        <IconButton aria-label="Close audit log" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <AuditLogContent auditLogs={auditLogs} isLoading={isLoading} />
      </DialogContent>
    </Dialog>
  )
}
